import { useAuth0 } from '@auth0/auth0-react';
import { useSitesWithMembers } from '@my/directory-api/hooks';
import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { ChevronDown, ChevronUp, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { HorizonField } from '../views/HorizonField';
import { LegIntelligence } from './components/LegIntelligence';
import { LegRoster } from './components/LegRoster';
import { LegsTable } from './components/LegsTable';
import {
  DRIVERS,
  intelFor,
  LEG_FILTERS,
  LEGS,
  matchesFilter,
  type GroupMode,
  type Leg,
  type LegFilter,
  type RosterDriver,
} from './lib/mockData';

const GROUP_MODES: GroupMode[] = ['Flat', 'Driver', 'Direction'];

/**
 * Leg Planning (Legs) — prototype. Manifest-style top bar (depot + horizon +
 * Group pills + search + MyI button), a filterable leg queue (Flat / by Driver
 * / by Direction), the sliding "my i" rail, and an always-visible driver
 * roster. FE-only — mock data, no backend.
 */
export default function LegsPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [legs, setLegs] = useState<Leg[]>(LEGS);
  const [filter, setFilter] = useState<LegFilter>('All');
  const [group, setGroup] = useState<GroupMode>('Flat');
  const [horizonDays, setHorizonDays] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dayDriverId, setDayDriverId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Real depot list — organisation Sites, name-sorted, auto-pick the first.
  const sitesQuery = useSitesWithMembers(orgId);
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  const [depotId, setDepotId] = useState<string | null>(null);
  useEffect(() => {
    const first = sites[0];
    if (!first) return;
    if (!depotId || !sites.some((s) => s.id === depotId)) setDepotId(first.id);
  }, [sites, depotId]);

  const ordered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let visible = legs.filter((l) => matchesFilter(l, filter));
    if (q)
      visible = visible.filter((l) =>
        [l.from, l.to, l.ref ?? '', l.driver ?? '', l.asset ?? ''].join(' ').toLowerCase().includes(q),
      );
    return visible.slice().sort((a, b) => (a.window ?? '99:99').localeCompare(b.window ?? '99:99'));
  }, [legs, filter, search]);

  const selected = useMemo(() => legs.find((l) => l.id === selectedId) ?? null, [legs, selectedId]);
  const assignMode = !!selected && selected.driver === null;
  const intel = useMemo(() => intelFor(selected), [selected]);
  const count = (f: LegFilter) => legs.filter((l) => matchesFilter(l, f)).length;

  const onSelect = (id: string) => {
    setSelectedId(id);
    setAssistantOpen(true); // selecting a leg loads it into the my i panel
  };

  const onAssign = (driverId: string) => {
    if (!selected || selected.driver !== null) return;
    const driver = DRIVERS.find((d) => d.id === driverId);
    if (!driver) return;
    setLegs((prev) =>
      prev.map((l) =>
        l.id === selected.id ? { ...l, driver: driver.name, status: 'Assigned', fit: 'On plan', fitTone: 'ok' } : l,
      ),
    );
  };

  const dayDriver = DRIVERS.find((d) => d.id === dayDriverId) ?? null;
  const dayLegs = dayDriver ? legs.filter((l) => l.driver === dayDriver.name) : [];

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      {/* Top bar — Manifest style: title band, depot, horizon, Group pills, search, MyI. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-slate-200 bg-white px-3 py-2">
        <h1 className="text-sm font-semibold text-slate-400">
          <span className="text-slate-700">Leg Planning</span>
        </h1>

        <Field label="Depot">
          <select
            value={depotId ?? ''}
            onChange={(e) => setDepotId(e.target.value || null)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {sites.length === 0 && <option value="">No sites</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <HorizonField value={horizonDays} onChange={setHorizonDays} />

        <div className="mx-1 h-7 w-px bg-slate-200" />

        <Field label="Group">
          <div className="flex items-center gap-1.5">
            {GROUP_MODES.map((g) => (
              <button key={g} type="button" onClick={() => setGroup(g)} className={legChipClass(g === group)}>
                {g}
              </button>
            ))}
          </div>
        </Field>

        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs…"
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
        >
          <Plus className="h-3.5 w-3.5" /> Add / assign leg
        </button>

        <button
          type="button"
          onClick={() => setAssistantOpen(true)}
          aria-label="Open movement intelligence"
          className={cn('shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-100', assistantOpen && 'opacity-40')}
        >
          <MyIIcon className="h-7 w-7 animate-pulse" />
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 px-1">
        {LEG_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              '-mb-px border-b-2 px-2.5 py-1.5 text-xs font-medium transition-colors',
              f === filter
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {f}
            {f !== 'All' && <span className="ml-1 text-slate-400">{count(f)}</span>}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-400">{legs.length} legs today</span>
      </div>

      {/* Leg queue table */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white">
        <LegsTable legs={ordered} group={group} drivers={DRIVERS} selectedId={selectedId} onSelect={onSelect} />
      </div>

      {assignMode && selected && (
        <div className="px-1 text-right text-[11px] font-medium text-emerald-700">
          Assign selected leg ({selected.id}) — click a driver card
        </div>
      )}

      <LegRoster drivers={DRIVERS} assignMode={assignMode} onAssign={onAssign} onOpenDay={setDayDriverId} />

      {/* Sliding "my i" panel — opened by the top-bar button / on leg select. */}
      <LegIntelligence open={assistantOpen} onClose={() => setAssistantOpen(false)} intel={intel} />

      {dayDriver && <DayDrawer driver={dayDriver} legs={dayLegs} onClose={() => setDayDriverId(null)} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </div>
  );
}

function legChipClass(active: boolean): string {
  return cn(
    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
  );
}

/** Driver day drawer — ordered leg sequence with (cosmetic) reorder + add task. */
function DayDrawer({
  driver,
  legs,
  onClose,
}: {
  driver: RosterDriver;
  legs: Leg[];
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-96 max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl">
        <header className="flex items-center justify-between bg-slate-800 px-4 py-3 text-white">
          <div className="leading-tight">
            <div className="text-sm font-semibold">{driver.name}</div>
            <div className="font-mono text-[10px] text-slate-300">
              {driver.reg} · {driver.startLabel}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-0.5 hover:bg-slate-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-2 overflow-auto p-3">
          {legs.length === 0 ? (
            <p className="py-6 text-center text-xs italic text-slate-400">No legs assigned today.</p>
          ) : (
            legs.map((l) => (
              <div key={l.id} className="flex items-center gap-2 rounded-md border border-slate-200 p-2 text-xs">
                <span className="w-10 shrink-0 font-mono text-[10px] text-slate-400">
                  {l.window?.slice(0, 5) ?? '—'}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-700">{l.type}</div>
                  <div className="truncate text-[11px] text-slate-500">
                    {l.from} → {l.to}
                  </div>
                </div>
                <div className="flex flex-col">
                  <button type="button" aria-label="Move up" className="text-slate-400 hover:text-slate-700">
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" aria-label="Move down" className="text-slate-400 hover:text-slate-700">
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <footer className="border-t border-slate-200 p-3">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add task
          </button>
        </footer>
      </aside>
    </>
  );
}
